import React, { useMemo } from "react";
import {
  Box,
  Text,
  Icon,
  HStack,
  VStack,
  Button,
  Divider,
} from "@chakra-ui/react";
import { FaTimes, FaFilter, FaChartPie } from "react-icons/fa";
import { MdBarChart, MdFunctions } from "react-icons/md";
import { LuGitCompare } from "react-icons/lu";
import { PiIntersectFill } from "react-icons/pi";

interface LanggraphMessageProps {
  langgraphMessage: string[];
  onClose: () => void;
  isSelected: boolean;
  section?: "query" | "calculations";
  hideCloseButton?: boolean;
  presetFiltersNode?: React.ReactNode;
  useParentScroll?: boolean;
}

/* -------------------- Types -------------------- */

type MetricDataScopeColumn = {
  datascope_name: string;
  output_column_name: string;
};

type DateDataScopeFilter = {
  datascope_name: string;
  start_date?: string;
  end_date?: string;
};

type AttributeDataScopeFilter = {
  datascope_name: string;
  operator: string;
  values: string[];
};

type GroupByDataScopeColumn = {
  datascope_name: string;
  output_column_name: string;
  time_bucket?: string;
};

type BenchmarkDataScopeColumn = {
  datascope_name: string;
  output_column_name: string;
  source_output_column_name: string;
};

type ComparisonDataScopeColumn = {
  output_column_name: string;
  metric_output_column_name: string;
  date_filter_modification?: DateDataScopeFilter;
  attribute_filter_modification?: AttributeDataScopeFilter;
};

type ExpressionColumn = {
  expression: string;
  output_column_name: string;
};

type ExpressionFilter = {
  expression: string;
};

type DataFrameToolArgs = {
  dataframe_name?: string;
  metric_columns?: MetricDataScopeColumn[];
  date_filters?: DateDataScopeFilter[];
  attribute_filters?: AttributeDataScopeFilter[];
  group_by_columns?: GroupByDataScopeColumn[];
  benchmark_columns?: BenchmarkDataScopeColumn[];
  comparison_columns?: ComparisonDataScopeColumn[];
  calculated_columns?: ExpressionColumn[];
  expression_filters?: ExpressionFilter[];
};

type ToolCall = {
  id: string;
  name: string;
  args?: DataFrameToolArgs | Record<string, any>;
  type?: string;
};

type ParsedToolCall = {
  id: string;
  name: string;
  args: DataFrameToolArgs | Record<string, any>;
};

/* -------------------- Helpers -------------------- */

function filterIsVisible(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => filterIsVisible(item));
  } else if (obj !== null && typeof obj === "object") {
    const filtered: Record<string, any> = {};
    Object.entries(obj).forEach(([key, value]) => {
      if (key !== "is_visible") {
        filtered[key] = filterIsVisible(value);
      }
    });
    return filtered;
  }
  return obj;
}

function parseLanggraphMessages(raw: string[]): ParsedToolCall[] {
  const parsed = raw
    .map((item) => {
      try {
        return JSON.parse(item);
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  const toolCalls: ParsedToolCall[] = [];
  console.log("parsed", parsed);

  parsed.forEach((msg: any) => {
    if (msg.type === "ai" && msg.tool_calls && Array.isArray(msg.tool_calls)) {
      msg.tool_calls.forEach((tc: ToolCall) => {
        if (tc.name && tc.args) {
          toolCalls.push({
            id: tc.id,
            name: tc.name,
            args: filterIsVisible(tc.args),
          });
        }
      });
    }
  });

  return toolCalls;
}

/* -------------------- Section Sub-components -------------------- */

const SectionHeader: React.FC<{ label: string; icon?: React.ElementType; iconSize?: number }> = ({
  label,
  icon: IconComponent,
  iconSize = 3,
}) => (
  <HStack spacing={2} mb={1} mt={0} align="center">
    {IconComponent && (
      <Icon as={IconComponent} boxSize={iconSize} color="purple.700" minW={4} />
    )}
    <Text
      fontSize="xs"
      fontWeight="600"
      color="gray.700"
      letterSpacing="0.04em"
      textAlign="left"
      textTransform="uppercase"
      mb={0}
    >
      {label}:
    </Text>
  </HStack>
);

const tagLikeStyles = {
  borderRadius: "md",
  bg: "white",
  borderWidth: "1px",
  borderColor: "gray.300",
  px: 1,
  py: 0.5,
  display: "inline" as const,
};

const BulletRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <HStack align="flex-start" spacing={1} pl={2} pt={0} mb={2}>
    <Text fontSize="xs" color="gray.700" fontFamily="mono" lineHeight="tall" textAlign="left" m={0} mb={0}>
      {children}
    </Text>
  </HStack>
);

const CompactDivider: React.FC = () => (
  <Divider borderColor="gray.200" my={1} />
);

/* -------------------- DataFrameToolCallCard -------------------- */

const DataFrameToolCallCard: React.FC<{
  args: DataFrameToolArgs;
  section?: "query" | "calculations";
  presetFiltersNode?: React.ReactNode;
}> = ({ args, section, presetFiltersNode }) => {
  const {
    metric_columns = [],
    group_by_columns = [],
    benchmark_columns = [],
    comparison_columns = [],
    calculated_columns = [],
    expression_filters = [],
  } = args;

  const showQuery = !section || section === "query";
  const showCalculations = !section || section === "calculations";

  const tableHelp = "in data table";

  return (
    <VStack align="stretch" spacing={2}>
      {showQuery && (
        <>

          {/* 1. Filters (preset) */}
          {presetFiltersNode && (
            <Box>
              <SectionHeader label="Apply filters" icon={FaFilter} />
              {presetFiltersNode}
              <CompactDivider />
            </Box>
          )}

          {/* 2. Metric Columns */}
          {metric_columns.length > 0 && (
            <Box>
              <SectionHeader label="Fetch metrics" icon={MdBarChart} iconSize={4} />
              {metric_columns.map((col, i) => (
                <BulletRow key={i}>
                  <Box as="span" fontWeight="600" color="gray.800" {...tagLikeStyles}>
                    {col.datascope_name}
                  </Box>
                  <Text as="span" color="gray.600" fontFamily="mono" fontSize="xs" ml={1} mb={0}>
                    (column <Text as="span" fontWeight="500" {...tagLikeStyles}>{col.output_column_name}</Text> {tableHelp})
                  </Text>
                </BulletRow>
              ))}
              <CompactDivider />
            </Box>
          )}

          {/* 3. Group By */}
          {group_by_columns.length > 0 && (
            <Box>
              <SectionHeader label="Group by" icon={FaChartPie} />
              {group_by_columns.map((col, i) => (
                <BulletRow key={i}>
                  <Box as="span" fontWeight="600" color="gray.800" {...tagLikeStyles}>
                    {col.datascope_name}
                  </Box>
                  <Text as="span" color="gray.600" fontFamily="mono" fontSize="xs" ml={1}>
                    (column "{col.output_column_name}" {tableHelp}
                    {col.time_bucket && (
                      <> with bucketing: <Text as="span" fontFamily="mono" fontSize="xs" color="gray.600" fontWeight="600">{col.time_bucket}</Text></>
                    )}
                    )
                  </Text>
                </BulletRow>
              ))}
              <CompactDivider />
            </Box>
          )}

          {/* 4. Comparison Columns — same metric with filter modifications */}
          {comparison_columns.length > 0 && (
            <Box>
              <SectionHeader label="Comparisons" icon={LuGitCompare} iconSize={4} />
              {comparison_columns.map((col, i) => {
                const hasDateMod = !!col.date_filter_modification;
                const hasAttrMod = !!col.attribute_filter_modification;
                const modifications: string[] = [];
                if (hasDateMod && col.date_filter_modification) {
                  const d = col.date_filter_modification;
                  modifications.push(
                    `${d.datascope_name ?? "Date"}: ${d.start_date ?? "—"} to ${d.end_date ?? "—"}`
                  );
                }
                if (hasAttrMod && col.attribute_filter_modification) {
                  const a = col.attribute_filter_modification;
                  const op = a.operator === "EQ" ? "=" : a.operator === "NE" ? "≠" : a.operator;
                  modifications.push(`${a.datascope_name} ${op} ${a.values?.join(", ") ?? ""}`);
                }
                const suffix = modifications.length > 0 ? ` with modified filters: ${modifications.join("; ")}` : "";
                return (
                  <BulletRow key={i}>
                    <Text as="span" fontWeight="500" color="gray.700">
                      Compare{" "}
                      <Box as="span" {...tagLikeStyles} fontWeight="600">{col.metric_output_column_name}</Box>
                      {" to "}
                      <Box as="span" {...tagLikeStyles} fontWeight="600">{col.output_column_name}</Box>
                    </Text>
                    {suffix && (
                      <Text color="gray.600" fontSize="xs" ml={1} mb={0}>
                        {suffix}
                      </Text>
                    )}
                  </BulletRow>
                );
              })}
              <CompactDivider />
            </Box>
          )}

          {/* 5. Benchmark Columns */}
          {benchmark_columns.length > 0 && (
            <Box>
              <SectionHeader label="Benchmark Columns" icon={MdBarChart} />
              {benchmark_columns.map((col, i) => (
                <BulletRow key={i}>
                  <Box as="span" fontWeight="600" color="gray.800" {...tagLikeStyles}>
                    {col.datascope_name}
                  </Box>
                  <Text as="span" color="gray.400" mx={1}>→</Text>
                  <Box as="span" color="gray.700" fontFamily="mono" fontSize="xs" {...tagLikeStyles}>
                    {col.output_column_name}
                  </Box>
                  <Text as="span" color="gray.500" fontSize="xs" ml={1}>
                    (source: <Text as="span" fontFamily="mono">{col.source_output_column_name}</Text>)
                  </Text>
                </BulletRow>
              ))}
              <CompactDivider />
            </Box>
          )}
        </>
      )}

      {showCalculations && (
        <>
          {calculated_columns.length > 0 && (
            <Box>
              <SectionHeader label="Apply formulae" icon={MdFunctions} iconSize={4} />
              {calculated_columns.map((col, i) => (
                <Box key={i} pl={2} mb={3} mt={2}>
                  <Text fontSize="xs" fontFamily="mono" textAlign="left" mb={0}>
                    <Box as="span" fontWeight="600" color="gray.800" {...tagLikeStyles}>
                      {col.output_column_name}
                    </Box>
                    <Text as="span" fontSize="xs" color="gray.600" ml={1} fontFamily="mono" textAlign="left" fontWeight="500" mb={0}>
                      = {col.expression}
                    </Text>
                  </Text>
                </Box>
              ))}
              <CompactDivider />
            </Box>
          )}

          {expression_filters.length > 0 && (
            <Box>
              <SectionHeader label="Only show rows with" icon={PiIntersectFill} iconSize={4} />
              {expression_filters.map((f, i) => (
                <Box key={i} pl={2} mt={2}>
                  <BulletRow key={i}>
                    <Box as="span" fontFamily="mono" fontSize="xs" color="gray.700" {...tagLikeStyles} fontWeight="600">
                      {f.expression}
                    </Box>
                  </BulletRow>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </VStack>
  );
};

/* -------------------- Generic Args Fallback -------------------- */

const GenericArgsDisplay: React.FC<{ args: Record<string, any> }> = ({
  args,
}) => (
  <>
    {Object.entries(args).map(([key, value]) => (
      <Text key={key} fontSize="sm" m={0} textAlign="left">
        <strong>{key}:</strong>{" "}
        {typeof value === "object"
          ? JSON.stringify(value, null, 2)
          : String(value)}
      </Text>
    ))}
  </>
);

/* -------------------- Component -------------------- */

const LanggraphMessageDisplayComponent: React.FC<
  LanggraphMessageProps
> = ({ langgraphMessage, onClose, isSelected, section, hideCloseButton, presetFiltersNode, useParentScroll }) => {
  const messages = useMemo(
    () => parseLanggraphMessages(langgraphMessage),
    [langgraphMessage]
  );

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" position="relative">
      {!hideCloseButton && (
        <Button
          size="sm"
          variant="ghost"
          color="red.500"
          _hover={{ color: "red.500", bg: "red.50" }}
          onClick={onClose}
          p={1}
          position="absolute"
          top={1}
          right={1}
          zIndex={1}
        >
          <Icon as={FaTimes} boxSize="3" />
        </Button>
      )}

      <Box
        flex="1"
        px={hideCloseButton ? 1 : 3}
        pt={hideCloseButton ? 1 : 6}
        overflow={useParentScroll ? "visible" : "auto"}
      >
        {messages.length === 0 && (
          <Text fontSize="sm" color="gray.500">
            No tool calls available.
          </Text>
        )}

        {messages.map((toolCall, index) => (
          <Box key={toolCall.id + index} mb={2} px={2}>
            {toolCall.name === "build_dataframe" ? (
              <DataFrameToolCallCard
                args={toolCall.args as DataFrameToolArgs}
                section={section}
                presetFiltersNode={presetFiltersNode}
              />
            ) : (
              toolCall.args &&
              Object.keys(toolCall.args).length > 0 && (
                <>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color="purple.600"
                    mt={2}
                    mb={1}
                    textAlign="left"
                  >
                    Arguments
                  </Text>
                  <GenericArgsDisplay
                    args={toolCall.args as Record<string, any>}
                  />
                </>
              )
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LanggraphMessageDisplayComponent;
